import {MAX_SEQUENCES_PER_ANNOTATION_REQUEST} from 'antibodies/domain-annotation/DomainAnnotationConstants';
import {DomainSequenceType} from 'antibodies/domain-annotation/DomainAnnotationTypes';
import {
  DOMAIN_INTERFACE_ID_BY_SEQUENCE_TYPE,
  getDomainSequenceType,
} from 'antibodies/domain-annotation/domainSchemaInterfaces';
import isArchived from 'archive/isArchived';
import {BioentitySummary} from 'folder-items/FileConstants';
import {TagSchemaInterfaceIdsById} from 'nav/sidepanel/listing/ItemListingUtil';
import * as RegistryUtil from 'registry/RegistryUtil';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {ListableItem} from 'search/SearchConstants';

/**
 * Returns the domain sequence type of a listing item whose schema implements the domain SSI for its sequence
 * type, or null when it cannot be annotated.
 */
export function getListingItemDomainSequenceType(
  item: ListableItem,
  tagSchemaInterfaceIdsById: TagSchemaInterfaceIdsById
): DomainSequenceType | null {
  if (item.tagSchema == null) {
    return null;
  }
  const sequenceType = getDomainSequenceType(item.tagSchema.itemType);
  const interfaceIds = tagSchemaInterfaceIdsById[item.tagSchema.id];
  if (
    sequenceType == null ||
    interfaceIds == null ||
    !interfaceIds.has(DOMAIN_INTERFACE_ID_BY_SEQUENCE_TYPE[sequenceType])
  ) {
    return null;
  }
  return sequenceType;
}

function isRegistered(item: ListableItem): boolean {
  const bioentity = item as BioentitySummary;
  return bioentity.folder != null && RegistryUtil.isRegistered(bioentity);
}

/** Returns why the selected listing items cannot be annotated together, or null when they can. */
export function getDomainAnnotationDisabledReason(
  items: Array<ListableItem>,
  tagSchemaInterfaceIdsById: TagSchemaInterfaceIdsById
): string | null {
  if (items.length === 0) {
    return null;
  }
  const sequenceTypes = new Set(
    items.map((item) => getListingItemDomainSequenceType(item, tagSchemaInterfaceIdsById))
  );
  if (sequenceTypes.has(null) || sequenceTypes.size > 1) {
    return 'Select only AA domain or only DNA domain sequences to annotate.';
  }
  if (items.length > MAX_SEQUENCES_PER_ANNOTATION_REQUEST) {
    return `At most ${MAX_SEQUENCES_PER_ANNOTATION_REQUEST} sequences can be annotated at once.`;
  }
  if (!items.every((item) => item.permissions.writable)) {
    return 'You need write access to the selected sequences to annotate them.';
  }
  if (!items.every((item) => isRegistered(item))) {
    return 'Sequences must be registered before they can be annotated.';
  }
  if (items.some((item) => isArchived(item))) {
    return 'Archived sequences cannot be annotated.';
  }
  if (items.some((item) => isFailed(item.schemaValidationState))) {
    return 'Sequences with registry validation errors cannot be annotated.';
  }
  if (new Set(items.map((item) => item.tagSchema?.registryId)).size > 1) {
    return 'Select sequences from a single registry to annotate.';
  }
  return null;
}
